/* global SERVER_URL */
import React, { useContext, useState, useEffect, Fragment, useMemo } from 'react';
import fetch from 'isomorphic-fetch';
import queryString from 'query-string';
import _ from 'lodash';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { SnapshotGalleryContext } from '../../contexts/SnapshotGalleryContext';
import ExploreAuth from '../../utils/ExploreAuth';
import { addQuery, checkHttpStatus, parseJSON, removeQuery, slugify } from '../../utils';
import { SnapshotGalleryItem } from './SnapshotGalleryItem';
import LightboxTitleInfo from './LightboxTitleInfo';
import LightboxCaption from './LightboxCaption';
import { deleteUserSnapshot, toggleUserFavoriteSnapshot } from '../../actions/favorites';
import Loader from '../Loader';


const SnapshotGallery = ({ currentUser, dispatch, favoriteSnapshots, createdBefore, isAuthenticated, currentUsername }) => {
  const { queryId, queryType } = useContext(SnapshotGalleryContext);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [favoriting, setFavoriting] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [preloadedSnapshot, setPreloadedSnapshot] = useState([]);
  const { ref, inView } = useInView({
    threshold: 0.9,
    delay: 1500,
  });
  const fetchData = async ({ pageParam }) => {
    const q = queryString.stringify({
      id: queryId,
      created_before: createdBefore,
      cursor: pageParam,
      per_page: 64,
    });
    const res = await ExploreAuth.getValidJwt()
        .then((tokenIsValid) => {
          let requestPromise = null;
          if (tokenIsValid) {
            requestPromise = fetch(`${queryType}?${q}`, {
              method: 'GET',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'x-www-form-urlencoded',
                Authorization: `Bearer ${ExploreAuth.getJwt()}`,
              },
            });
          } else {
            requestPromise = fetch(`${queryType}?${q}`);
          }
          return requestPromise.then(checkHttpStatus)
            .then(parseJSON)
            .catch((error) => {
              console.log(error, 'An error occurred');
            });
        });
    return res;
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetched,
    isSuccess,
    isLoading,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: [queryType, queryId, createdBefore],
    queryFn: fetchData,
    initialPageParam: '',
    getNextPageParam: lastPage => lastPage.meta.next_cursor,
  });

  useEffect(() => {
    // if the last element is in view and there is a next page, fetch the next page
    if (inView && hasNextPage && queryType) {
      fetchNextPage();
    }
  }, [hasNextPage, inView, fetchNextPage]);

  const toggleFavorite = (snapshotUuid) => {
    if (!favoriting.includes(snapshotUuid)) {
      setFavoriting([...favoriting, snapshotUuid]);
      dispatch(toggleUserFavoriteSnapshot(snapshotUuid))
        .then(() => {
          const updatedFavoriting = _.remove(favoriting, v => v === snapshotUuid);
          setFavoriting(updatedFavoriting);
        });
    }
  };

  const canSeeTrashIcon = (snapshot) => {
    if (!currentUser) {
      return false;
    }
    const { partner_livecams: partnerLivecams = [], isPartner, isStaff } = currentUser;

    if (isStaff) {
      return true;
    }

    if (isPartner && partnerLivecams.includes(snapshot.livecam_id)) {
      return true;
    }
    return currentUsername === snapshot.username;
  };

  const trashSnapshot = (snapshotUuid) => {
    const curr = browserHistory.getCurrentLocation();
    setDeleting(true);
    dispatch(deleteUserSnapshot(snapshotUuid))
      .then(() => {
        setDeleting(false);
        alert('Snapshot deleted');
        window.location = curr.pathname;
      })
      .catch(() => {
        alert('Error deleting snapshot');
        setDeleting(false);
      });
  };

  const lightboxSlide = snapshot => (
    {
      src: snapshot.snapshot,
      uuid: snapshot.uuid,
      title: (<LightboxTitleInfo snapshot={snapshot} />),
      description: (
        <LightboxCaption
          snapshot={snapshot}
          canSeeTrashIcon={canSeeTrashIcon(snapshot)}
          deleting={deleting}
          favoriting={favoriting}
          onFavorite={() => toggleFavorite(snapshot.uuid)}
          onDelete={() => {
            if (window.confirm('Are you sure you want to delete this snapshot? This cannot be undone.')) {
              trashSnapshot(snapshot.uuid);
            }
          }}
        />
      ),
    }
  );

  useEffect(() => {
    const location = Object.assign({}, browserHistory.getCurrentLocation());
    if (location.query.id || location.query.snapshot) {
      const queryData = location.query.id ? { legacy_id: location.query.id } : { uuid: location.query.snapshot };
      const query = queryString.stringify(queryData);

      ExploreAuth.getValidJwt()
        .then((tokenIsValid) => {
          let requestPromise = null;
          if (tokenIsValid) {
            requestPromise = fetch(`${SERVER_URL}/snapshots/single?${query}`, {
              method: 'GET',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'x-www-form-urlencoded',
                Authorization: `Bearer ${ExploreAuth.getJwt()}`,
              },
            });
          } else {
            requestPromise = fetch(`${SERVER_URL}/snapshots/single?${query}`);
          }

          requestPromise.then(checkHttpStatus)
            .then(parseJSON)
            .then((response) => {
              if (response.data) {
                setPreloadedSnapshot([lightboxSlide(response.data)]);
                setLightboxIndex(0);
                addQuery({ snapshot: response.data.uuid });
                removeQuery('id');
              }
            })
            .catch((singleSnapshotError) => {
              console.log(singleSnapshotError, 'An error occurred');
            });
        });
    }
  }, []);

  if (error) {
    return (
      <div className="mt-10">
        {`An error has occurred: ${error.message}`}
      </div>
    );
  }
  const slides = useMemo(() => {
    const lightboxSlides = data ? data?.pages.map(group => group.data.map(lightboxSlide)).flat() : [];
    return [...preloadedSnapshot, ...lightboxSlides];
  }, [data?.pages.length, queryType, queryId, preloadedSnapshot]);

  const toggleLightbox = (uuid) => {
    const index = slides.findIndex(slide => slide.uuid === uuid);
    setLightboxIndex(index);
  };

  return (
    <Fragment>
      <div className="grid row no-gutters snapshot-grid">
        {isSuccess && data?.pages.map((group, i) => (
          <Fragment key={i}>
            {group.data.map((snapshot, index) => {
              const isFavorite = favoriteSnapshots.includes(snapshot.uuid);
              const activeClass = (isFavorite) ? 'fas favorited' : 'far';
              return (group.data.length === index + 1) ?
                (
                  <div key={snapshot.uuid} ref={ref} className="grid-item col-6 col-lg-4 col-xl-3 col-xxl-2">
                    <SnapshotGalleryItem
                      dataSnapshotIndex={index}
                      snapshot={snapshot}
                      toggleLightbox={() => { toggleLightbox(snapshot.uuid); }}
                      favoriting={favoriting}
                      activeClass={activeClass}
                      toggleFavorite={() => toggleFavorite(snapshot.uuid)}
                      authenticated={isAuthenticated}
                    />
                  </div>
                ) :
                (
                  <div key={snapshot.uuid} className="grid-item col-6 col-lg-4 col-xl-3 col-xxl-2">
                    <SnapshotGalleryItem
                      dataSnapshotIndex={index}
                      snapshot={snapshot}
                      toggleLightbox={() => { toggleLightbox(snapshot.uuid); }}
                      favoriting={favoriting}
                      activeClass={activeClass}
                      toggleFavorite={() => toggleFavorite(snapshot.uuid)}
                      authenticated={isAuthenticated}
                    />
                  </div>
                );
            })}
          </Fragment>
        ))}
      </div>
      <Lightbox
        plugins={[Captions]}
        index={lightboxIndex}
        slides={slides}
        open={lightboxIndex >= 0}
        close={() => {
          setLightboxIndex(-1);
          removeQuery('snapshot');
        }}
        on={{
          view: ({ index: slideIndex }) => {
            addQuery({ snapshot: slides[slideIndex].uuid });
          },
        }}
      />
      {(isLoading || isFetchingNextPage) && (
        <div className="loader"><Loader dark /></div>
      )}
      {isSuccess && isFetched && !hasNextPage && (data?.pages.flatMap(i => i.data.length).reduce((accumulator, currentValue) => currentValue + accumulator) === 0) && (<h4>No Snapshots Found</h4>)}
    </Fragment>);
};
const mapStateToPops = (state) => {
  const { favorites, auth, currentUser } = state;
  return {
    favoriteSnapshots: _.get(favorites, 'user.snapshots', []) || [],
    currentUsername: _.get(auth, 'userName', null),
    currentUser: _.get(currentUser, 'data', {}),
    isAuthenticated: state.auth.isAuthenticated,
  };
};
export default connect(mapStateToPops)(SnapshotGallery);
